import { isProduction } from './environment-helper';

// Sitemap helper functions - only work in production
class SitemapHelper {
  static baseUrl = 'https://www.coachingsearchengine.com';

  // Generate URL entry for sitemap
  static createUrlEntry(url, lastmod = null, changefreq = 'weekly', priority = 0.5) {
    if (!isProduction()) return '';

    const lastmodStr = lastmod ? new Date(lastmod).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

    return `
  <url>
    <loc>${url}</loc>
    <lastmod>${lastmodStr}</lastmod>
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
  }

  // Generate static pages sitemap entries
  static generateStaticPages() {
    if (!isProduction()) return '';

    const staticPages = [
      // Homepage
      { url: `${this.baseUrl}/tr`, priority: 1.0, changefreq: 'daily' },
      { url: `${this.baseUrl}/en`, priority: 1.0, changefreq: 'daily' },

      // Categories pages
      { url: `${this.baseUrl}/tr/kategoriler`, priority: 0.9, changefreq: 'weekly' },
      { url: `${this.baseUrl}/en/categories`, priority: 0.9, changefreq: 'weekly' },

      // Legal pages
      { url: `${this.baseUrl}/tr/gizlilik-politikasi`, priority: 0.3, changefreq: 'monthly' },
      { url: `${this.baseUrl}/en/privacy-policy`, priority: 0.3, changefreq: 'monthly' },
      { url: `${this.baseUrl}/tr/kullanim-sartlari`, priority: 0.3, changefreq: 'monthly' },
      { url: `${this.baseUrl}/en/terms-and-conditions`, priority: 0.3, changefreq: 'monthly' },
    ];

    return staticPages.map((page) => this.createUrlEntry(page.url, null, page.changefreq, page.priority)).join('');
  }

  // Generate category pages sitemap entries
  static generateCategoryPages(categories) {
    if (!isProduction() || !categories || !Array.isArray(categories)) return '';

    let categoryEntries = '';

    categories.forEach((category) => {
      // Ana kategoriler için her iki dilde URL oluştur
      if (category.parentId === '0') {
        // Turkish URL
        const trUrl = `${this.baseUrl}/tr/kategori/${category.id}-id-${this.createSlug(category.nameTr)}`;
        categoryEntries += this.createUrlEntry(trUrl, null, 'weekly', 0.8);

        // English URL
        const enUrl = `${this.baseUrl}/en/category/${category.id}-id-${this.createSlug(category.nameEn)}`;
        categoryEntries += this.createUrlEntry(enUrl, null, 'weekly', 0.8);
      }

      // Alt kategoriler
      if (category.categories && category.categories.length > 0) {
        category.categories.forEach((subCategory) => {
          // Turkish URL
          const trUrl = `${this.baseUrl}/tr/kategori/${subCategory.id}-id-${this.createSlug(subCategory.nameTr)}`;
          categoryEntries += this.createUrlEntry(trUrl, null, 'weekly', 0.7);

          // English URL
          const enUrl = `${this.baseUrl}/en/category/${subCategory.id}-id-${this.createSlug(subCategory.nameEn)}`;
          categoryEntries += this.createUrlEntry(enUrl, null, 'weekly', 0.7);
        });
      }
    });

    return categoryEntries;
  }

  // Generate coach pages sitemap entries
  static generateCoachPages(coaches) {
    if (!isProduction() || !coaches || !Array.isArray(coaches)) return '';

    return coaches
      .map((coach) => {
        const coachSlug = this.generateCoachSlug(coach);

        // Turkish URL
        const trUrl = `${this.baseUrl}/tr/coach/${coachSlug}`;
        const trEntry = this.createUrlEntry(trUrl, coach.updatedAt, 'monthly', 0.6);

        // English URL
        const enUrl = `${this.baseUrl}/en/coach/${coachSlug}`;
        const enEntry = this.createUrlEntry(enUrl, coach.updatedAt, 'monthly', 0.6);

        return trEntry + enEntry;
      })
      .join('');
  }

  // Helper function to create slug (similar to category model)
  static createSlug(text) {
    if (!text) return '';

    return text
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  // Helper function to generate coach slug
  static generateCoachSlug(coach) {
    if (!coach || !coach.firstName || !coach.lastName || !coach.id) {
      return '';
    }

    const firstName = this.createSlug(coach.firstName);
    const lastName = this.createSlug(coach.lastName);

    return `${firstName}-${lastName}-${coach.id}`;
  }

  // Generate complete sitemap XML
  static generateSitemap(categories = [], coaches = []) {
    if (!isProduction()) return '';

    const xmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`;

    const xmlFooter = `
</urlset>`;

    const staticPages = this.generateStaticPages();
    const categoryPages = this.generateCategoryPages(categories);
    const coachPages = this.generateCoachPages(coaches);

    return xmlHeader + staticPages + categoryPages + coachPages + xmlFooter;
  }

  // Save sitemap to public folder (for development/build)
  static async saveSitemap(categories = [], coaches = []) {
    if (!isProduction()) {
      console.log('Sitemap generation skipped - not in production environment');
      return;
    }

    try {
      const sitemapContent = this.generateSitemap(categories, coaches);

      // Bu browser ortamında çalışmayacak, sadece build zamanında kullanılabilir
      console.log('Sitemap generated successfully');
      console.log('URLs count:', {
        categories: categories.length * 2, // Turkish + English
        coaches: coaches.length * 2, // Turkish + English
        static: 8, // Homepage, categories, legal pages
      });

      return sitemapContent;
    } catch (error) {
      console.error('Error generating sitemap:', error);
    }
  }

  // Get sitemap status
  static getSitemapInfo(categories = [], coaches = []) {
    if (!isProduction()) return null;

    const totalUrls = 8 + categories.length * 2 + coaches.length * 2;

    return {
      totalUrls,
      staticPages: 8,
      categoryPages: categories.length * 2,
      coachPages: coaches.length * 2,
      lastGenerated: new Date().toISOString(),
      sitemapUrl: `${this.baseUrl}/sitemap.xml`,
    };
  }

  // Generate sitemap index (if needed for large sites)
  static generateSitemapIndex() {
    if (!isProduction()) return '';

    const xmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`;

    const xmlFooter = `
</sitemapindex>`;

    const lastmod = new Date().toISOString().split('T')[0];

    const sitemapEntry = `
  <sitemap>
    <loc>${this.baseUrl}/sitemap.xml</loc>
    <lastmod>${lastmod}</lastmod>
  </sitemap>`;

    return xmlHeader + sitemapEntry + xmlFooter;
  }
}

export default SitemapHelper;
